
<template>
  <div v-if="[1, 2, 3, 4, 5, 6].includes(numberOfBars)"
  class="notation notation--middle"
  id="notation"
  ></div>
  <div 
  class="w-full"
  v-else>
    <div
    class="notation notation--top"
    id="notationTop"
    >
    </div>
    <div
    class="notation notation--bottom"
    id="notationBottom"
    ></div>
  </div>
</template>

<script setup>
import abcjs from "abcjs"
import 'abcjs/abcjs-audio.css'

const playerStore = usePlayerStore()
const userStore = useUserStore()
const { profile } = storeToRefs(userStore)
const { barPlaying, currentStage, handsAreSwitched } = storeToRefs(playerStore)

playerStore.resetHands()

const props = defineProps({
  data: {
    type: Object
  },
  id: {
    type: Number,
    required: true
  },
  performanceMode: {
    type: Boolean,
    default: false
  }
})

const svgViewBox = ref(null)

const stick = ref(props.data.rudiment_abc_stick || null)

watch(() => handsAreSwitched.value, (newValue, oldValue) => {
  if (stick.value) {
    stick.value = stick.value.replace(/R/g, 'X').replace(/L/g, 'R').replace(/X/g, 'L')
    stickSetup()
    renderBars()
  }
})

playerStore.stage(0)

const rudiment = props.data

let beat, subdivision, notes
beat = props.data?.meter_beat || 4
subdivision = props.data?.meter_subdivision || 4
notes = props.data.rudiment_abc_notes

// switch R and L
if (profile.value?.left_handed) {
  if (stick.value) stick.value = stick.value.replace(/R/g, 'X').replace(/L/g, 'R').replace(/X/g, 'L')
}


const notationSetup = `X:1
L:1/4
M:${beat}/${subdivision}
I:linebreak $
K:C
V:1 perc stafflines=1 stem=up
K:none
%MIDI drummap E 64
I:percmap E B 64 normal
V:1
`

let barsArray = notes.split("|")
if (barsArray.length > 1) {
  if (barsArray[barsArray.length - 1].includes(']')) {
    barsArray.pop()
  }
}

const numberOfBars = barsArray.length

let maxWidth = numberOfBars < 2 ? 335 : 488
// adjust for smaller bars
if (numberOfBars < 2 && beat == 2 && subdivision == 4) {
  if (notes.length > 20) {
    maxWidth = 320 // 320
  } else {
    maxWidth = 240
  }
} else if (numberOfBars >= 4 && notes.length > 200) {
  maxWidth = 600
}

if (props.data?.maxWidth) {
  maxWidth = props.data?.maxWidth
}

let measuresPerLine = 2
if (numberOfBars === 3 || numberOfBars === 5 || numberOfBars === 6) {
  measuresPerLine = 3
  maxWidth = maxWidth * 1.5
}

let stickArray
const stickLinesArray = ref([])
const stickSetup = () => {

  stickArray = []
  if (stick.value) {
    stickArray = stick.value.split("|")
  }
  stickArray.pop()

  stickLinesArray.value = []

  for (let i = 0; i < stickArray.length; i += measuresPerLine) {
    stickLinesArray.value.push(stickArray.slice(i, i + measuresPerLine))
  }
}
stickSetup()

const linesArray = []

for (let i = 0; i < barsArray.length; i += measuresPerLine) {
  linesArray.push(barsArray.slice(i, i + measuresPerLine))
}
if (linesArray.length > 1) {
  linesArray[linesArray.length - 1][linesArray[linesArray.length - 1].length - 1] += "]";
}


// All options can be found here: https://paulrosen.github.io/abcjs/visual/render-abc-options.html
const abcjsOptions = {
  minSpacing: 1.5,
  // oneSvgPerLine: true,
  dragging: false,
  responsive: "resize",
  selectTypes: [],
  // scale: 2,
  viewportHorizontal: true,
  lineThickness: 0.3,
  ariaLabel: false,
  add_classes: true,
  // viewportVertical: true,
  staffwidth: maxWidth,
  wrap: {
    preferredMeasuresPerLine: measuresPerLine,
    rightMargin: 0,
    lastLineLimit: 1,
    useSvgPath: true
  }
}

let firstRender = true
let renderArray = [
  { top: 0, bottom: 1 },
  { top: 0, bottom: 1 },
  { top: 2, bottom: 1 },
  { top: 2, bottom: 1 },
  { top: 2, bottom: 3 },
  { top: 2, bottom: 3 },
  { top: 4, bottom: 3 },
  { top: 4, bottom: 3 },
  { top: 4, bottom: 5 },
  { top: 4, bottom: 5 },
  { top: 6, bottom: 5 },
  { top: 6, bottom: 5 },
  { top: 6, bottom: 7 },
  { top: 6, bottom: 7 },
  { top: 8, bottom: 7 },
  { top: 8, bottom: 7 },
  { top: 8, bottom: 9 },
  { top: 8, bottom: 9 },
  { top: 10, bottom: 9 },
  { top: 10, bottom: 9 },
  { top: 10, bottom: 11 },
  { top: 10, bottom: 11 },
  { top: 12, bottom: 11 },
  { top: 12, bottom: 11 },
  { top: 12, bottom: 13 },
  { top: 12, bottom: 13 },
  { top: 14, bottom: 13 },
  { top: 14, bottom: 13 },
  { top: 14, bottom: 15 },
  { top: 14, bottom: 15 },
  { top: 16, bottom: 15 },
  { top: 16, bottom: 15 },
  { top: 16, bottom: 17 },
  { top: 16, bottom: 17 },
  { top: 18, bottom: 17 },
  { top: 18, bottom: 17 },
  { top: 18, bottom: 19 },
  { top: 18, bottom: 19 },
  { top: 20, bottom: 19 },
  { top: 20, bottom: 19 },
  { top: 20, bottom: 21 },
  { top: 20, bottom: 21 },
  { top: 22, bottom: 21 },
  { top: 22, bottom: 21 },
  { top: 22, bottom: 23 },
  { top: 22, bottom: 23 },
  { top: 24, bottom: 23 },
  { top: 24, bottom: 23 },
  { top: 24, bottom: 25 },
  { top: 24, bottom: 25 },
  { top: 26, bottom: 25 },
  { top: 26, bottom: 25 },
  { top: 26, bottom: 27 },
  { top: 26, bottom: 27 },
  { top: 28, bottom: 27 },
  { top: 28, bottom: 27 },
  { top: 28, bottom: 29 },
  { top: 28, bottom: 29 },
  { top: 30, bottom: 29 },
  { top: 30, bottom: 29 },
  { top: 30, bottom: 31 },
  { top: 30, bottom: 31 },
  { top: 32, bottom: 31 },
  { top: 32, bottom: 31 },
  { top: 32, bottom: 33 },
  { top: 32, bottom: 33 },
  { top: 34, bottom: 33 },
  { top: 34, bottom: 33 },
  { top: 34, bottom: 35 },
  { top: 34, bottom: 35 },
  { top: 36, bottom: 35 },
  { top: 36, bottom: 35 },
  { top: 36, bottom: 37 },
  { top: 36, bottom: 37 },
  { top: 38, bottom: 37 },
  { top: 38, bottom: 37 },
  { top: 38, bottom: 39 },
  { top: 38, bottom: 39 },
  { top: 40, bottom: 39 },
  { top: 40, bottom: 39 },
  { top: 40, bottom: 41 },
  { top: 40, bottom: 41 },
  { top: 42, bottom: 41 },
  { top: 42, bottom: 41 },
  { top: 42, bottom: 43 },
  { top: 42, bottom: 43 },
  { top: 44, bottom: 43 },
  { top: 44, bottom: 43 }
  // for longer pieces we will need to extend this array
]

const renderBars = () => {
  if (playerStore.barPlaying >= numberOfBars) {
    playerStore.barPlaying = 0
  }

  const currentBar = playerStore.barPlaying

  if (linesArray.length <= 2) {

    firstRender = false
    const barsToRender = barsArray
    const stickToRender = stickArray
    const linesToRender = []

    // Splitting the bars into two lines if more than 2 bars
    for (let i = 0; i < barsToRender.length; i += measuresPerLine) {
      linesToRender.push(barsToRender.slice(i, i + measuresPerLine).join("|") + "|")
      if (stickArray.length) {
        linesToRender.push('w: '+stickToRender.slice(i, i + measuresPerLine).join("|") + "|")
      }
    }

    const render = notationSetup + linesToRender.join("\n")
    // console.log(render)
    abcjs.renderAbc("notation", render, abcjsOptions)
    adjustSVG()
    return
  }

  else if (linesArray.length > 1) {

    if (linesArray[renderArray[currentBar].top] == undefined) {
      abcjs.renderAbc("notationTop", '', abcjsOptions)
      return
    }
    if (linesArray[renderArray[currentBar].bottom] == undefined) {
      abcjs.renderAbc("notationBottom", '', abcjsOptions)
      return
    }

    const topBars = linesArray[renderArray[currentBar].top]
    const bottomBars = linesArray[renderArray[currentBar].bottom]

    const topSticks = stickLinesArray.value[renderArray[currentBar].top]
    const bottomSticks = stickLinesArray.value[renderArray[currentBar].bottom]

    const topLinesToRender = []

    let topBarsJoin = topBars.join("|") + "|"
    if (topBarsJoin.includes(']|')) {
      topBarsJoin = topBarsJoin.replace(/]\|/g, "|]");
    }
    
    topLinesToRender.push(topBarsJoin)

    if (stickArray.length) {
      topLinesToRender.push('w: ' + topSticks.join("|") + "|")
    }
    const topRender = notationSetup + topLinesToRender.join("\n")
    abcjs.renderAbc("notationTop", topRender, abcjsOptions)

    const bottomLinesToRender = []
    let bottomBarsJoin = bottomBars.join("|") + "|"
    if (bottomBarsJoin.includes(']')) {
      bottomBarsJoin = bottomBarsJoin.replace(/]\|/g, "|]")
    }
    bottomLinesToRender.push(bottomBarsJoin)
    if (stickArray.length) {
      bottomLinesToRender.push('w: ' + bottomSticks.join("|") + "|")
    }
    const bottomRender = notationSetup + bottomLinesToRender.join("\n")
    abcjs.renderAbc("notationBottom", bottomRender, abcjsOptions)

    adjustSVG()

  }

}

const adjustSVG = () => {
  const svg = document.querySelector('.notation svg')
  if (!svg || svg.length === 0) return

  const playerView = document.querySelector('.player-view')
  if (playerView) {
    document.querySelector('.player-view').classList.add(`bars-${numberOfBars}`)
  }
  
  const triplet = svg.querySelector('g[data-name="triplet"]')
  if (triplet) {
    svg.classList.add('triplet')
  }

  // if (!props.performanceMode && !profile.value?.color_hands) {
  //   return
  // }
  if (!profile.value?.color_hands) {
    return
  }


  document.querySelectorAll('.abcjs-lyric tspan').forEach(tspan => {
    const colorBlue = '#4D7EEE'
    const colorRed = '#D44431'

    const text = tspan
    const x = parseFloat(text.getAttribute('x'))
    const y = parseFloat(text.parentElement.getAttribute('y'))
    
    if (text.textContent === 'L' || text.textContent === 'R' || text.textContent === 'T') {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      circle.setAttribute('cx', x + 4)
      circle.setAttribute('cy', y - 1 )
      circle.setAttribute('r', '6')
      
      if (text.textContent === 'L') {
        circle.setAttribute('fill', colorBlue)
      } else if (text.textContent === 'R') {
        circle.setAttribute('fill', colorRed)
      } else {
        circle.setAttribute('fill', 'black')
      }
      
      text.parentElement.parentElement.insertBefore(circle, text.parentElement)
      text.style.fill = 'white'
      text.style.fontSize = '0.6rem'
    }

  })

  if (numberOfBars < 2) {
    document.querySelector('.notation').classList.add('one-bar')
  } else {
    document.querySelector('.notation').classList.add('more-bars')
  }

  if (!svgViewBox.value) {
    document.querySelectorAll('.abcjs-container svg').forEach((item, index) => {
      if (!svgViewBox.value) {
        const viewBox = item.getAttribute('viewBox').split(' ')
        // viewBox[3] = (parseFloat(viewBox[3]) + 2).toString()
        viewBox[3] = (parseFloat(viewBox[3])).toString()
        const theViewBox = viewBox.join(' ')
        item.setAttribute('viewBox', theViewBox)
        svgViewBox.value = theViewBox
        // console.log('theViewBox', svgViewBox.value)
      }
    })
  }

  if (numberOfBars > 2) {
    const content = svg.querySelector('.abcjs-staff-wrapper')   
    document.querySelectorAll('.abcjs-container svg').forEach((item, index) => {
      item.setAttribute('viewBox', svgViewBox.value)
      item.setAttribute('preserveAspectRatio', 'xMidYMid meet')
      // console.log('set', svgViewBox.value)
    })
  }

}

watch(() => playerStore.barPlaying, (newValue, oldValue) => {
  renderBars()
})

onMounted(() => {
  renderBars()
  setTimeout(() => {
    adjustSVG()
  }, 300)
})

</script> 

<style lang="scss">
:root {
  --notation-scale: 1.5; /* Adjust this value to change the overall size */
}

.one-bar {
  @media (max-width: 919px) and (orientation: landscape) {
    width: 355px!important;
  }
}

svg.triplet {
  @media (min-width: 920px) {
    margin-top: -30px;
  }
  @media (max-width: 919px) and (max-height: 349px) and (orientation: landscape) {
    padding-left: 40px;
    padding-right: 40px;
  }
  @media (max-width: 919px) and (min-height: 350px) and (max-height: 374px) and (orientation: landscape) {
    padding-left: 30px;
    padding-right: 30px;
  }
  @media (max-width: 919px) and (min-height: 375px) and (orientation: landscape) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.more-bars {
  @media (max-width: 919px) and (max-height: 349px) and (orientation: landscape) {
    width: 500px!important;
  }
  @media (max-width: 919px) and (min-height: 350px) and (max-height: 374px) and (orientation: landscape) {
    width: 550px!important;
  }
  @media (max-width: 919px) and (min-height: 375px) and (orientation: landscape) {
    width: 620px!important;
  }
}

.notation {
  width: 100%!important;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: auto !important; /* Override any inline width */
    height: auto !important; /* Override any inline height */
    display: block;

    text {
      font-family: 'NBA', Arial;
      font-weight: normal;
      font-size: 12px;
    }
    g[data-name="note"] {
      position: relative;
      text {
        transform: translate(4px, 0.2em);
      }
    }
    g[data-name="triplet"] {
      transform: translate(4px);
    }
    g[data-name="dynamics"] {
      transform: translateY(5px);
    }
    path[data-name="dynamics"] {
      transform: translateY(5px);
    }
    path[highlight="stroke"] {
      transform: translateY(8px);
    }
    path[data-name="roll"] {
      transform: translate(6px, 13px);
    }
    path[data-name="scripts.roll"] {
      transform: translate(6px, 13px);
    }
  }
}

.abcjs-staff-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.abcjs-inner {
  overflow: visible!important;
  div {
    overflow: visible!important;
  }
}

.abcjs-inner {
  overflow: visible !important;
  div {
    overflow: visible !important;
  }
}

.abcjs-container {
  > div {
    overflow: visible !important;
  }
}

.abcjs-meta-top {
  display: none;
}

text.abcjs-lyric tspan {
  font-family: 'NBA', Courier New;
}

.player-view {
  margin-right: auto;
  margin-left: auto;
}


@media (max-width: 919px) and (max-height: 350px) {
  .player-view {
    max-width: 500px;
  }
}

@media (max-width: 919px) and (min-height: 351px) and (max-height: 400px) {
  .player-view {
    max-width: 600px;
  }
}

@media (max-width: 919px) and (min-height: 401px) {
  .player-view {
    max-width: 700px;
  }
}

@media (min-width: 919px) and (max-height: 500px) {
  .player-view {
    max-width: 400px;
  }
}

@media (min-width: 919px) and (min-height: 501px) and (max-height: 600px) {
  .player-view {
    max-width: 500px;
  }
}

@media (min-width: 919px) and (min-height: 601px) and (max-height: 700px) {
  .player-view {
    max-width: 600px;
  }
}

@media (min-width: 919px) and (min-height: 701px) and (max-height: 900px) {
  .player-view {
    max-width: 1000px;
  }
  .player-view.bars-1 {
    max-width: 800px;
  }
}

@media (min-width: 919px) and (min-height: 901px) and (max-height: 1000px) {
  .player-view {
    max-width: 1300px;
  }
  .player-view.bars-1 {
    max-width: 900px;
  }
}

@media (min-width: 919px) and (min-height: 1001px) {
  .player-view {
    max-width: 1400px;
  }
  .player-view.bars-1 {
    max-width: 900px;
  }
}
</style>